import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authlog } from 'AuthLogger';
import { pageDataService } from 'common/utilities/PageDataService';
import { debounce } from 'lodash';
import { useWebView } from 'store/webView/useWebView';

import { CM_SESSION_IDLE_TIMEOUT } from './CreditMonitoringConstants';

export enum IdleState {
  ACTIVE = 'active',
  IDLE = 'idle',
}

const USER_ACTIVITY_EVENTS = [
  'click',
  'keydown',
  'mousewheel',
  'touchstart',

  // Purposely not used:
  // 'mousemove',
  // 'touchmove',

  // Too noisy with click listener:
  // 'mousedown',

  // Too noisy because it fires events when you switch back to CM page:
  // 'focus',
];

interface IIdleDetectionState {
  isActive: boolean;
  isIdle: boolean;
  lastActiveTime: number;
  reactivate: () => void;
}

const defaultIdleDetectionState: IIdleDetectionState = {
  isActive: true,
  isIdle: false,
  lastActiveTime: Date.now(),
  reactivate: () => {},
};

export const IdleDetectionContext = createContext<IIdleDetectionState>(defaultIdleDetectionState);

interface IIdleDetectionProviderProps {
  children: JSX.Element;
}

const useIdleDetection = () => {
  const [activityState, setActivityState] = useState(IdleState.ACTIVE);
  const [lastActiveTime, setLastActiveTime] = useState(Date.now());
  const idleTimeoutId = useRef<NodeJS.Timeout | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateLastActiveTime = useCallback(
    debounce(
      () => {
        authlog('IdleDetectionProvider: user activity detected -> updating lastActiveTime');
        setLastActiveTime(Date.now());
      },
      5000 // 5 sec
    ),
    []
  );

  const reactivate = useCallback(() => {
    setActivityState(IdleState.ACTIVE);
    updateLastActiveTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bind user activity events to the reactivate function
  useEffect(() => {
    USER_ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, reactivate, true);
    });

    return () => {
      USER_ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, reactivate, true);
      });
    };
  }, [reactivate]);

  // Every time the user activates, set up an automatic transition
  // to IDLE after IDLE_TIMEOUT has elapsed.
  useEffect(() => {
    if (activityState === IdleState.ACTIVE) {
      if (idleTimeoutId.current) {
        clearTimeout(idleTimeoutId.current);
      }
      idleTimeoutId.current = setTimeout(() => {
        setActivityState(IdleState.IDLE);
      }, CM_SESSION_IDLE_TIMEOUT);
    }

    return () => {
      if (idleTimeoutId.current) {
        clearTimeout(idleTimeoutId.current);
      }
    };
  }, [activityState]);

  const contextValue = useMemo(
    () => ({
      isActive: activityState === IdleState.ACTIVE,
      isIdle: activityState === IdleState.IDLE,
      lastActiveTime,
      reactivate,
    }),
    [activityState, lastActiveTime, reactivate]
  );

  return contextValue;
};

/**
 * Provider that detects user inactivity and sets the state accordingly.
 * Active state can also be reactivated manually.
 * @param param0
 * @param param0.children
 */
const IdleDetectionProviderInternal = ({ children }: IIdleDetectionProviderProps) => {
  const contextValue = useIdleDetection();
  return <IdleDetectionContext.Provider value={contextValue}>{children}</IdleDetectionContext.Provider>;
};

const NoopProvider = ({ children }: IIdleDetectionProviderProps) => {
  return <IdleDetectionContext.Provider value={defaultIdleDetectionState}>{children}</IdleDetectionContext.Provider>;
};

export const IdleDetectionProvider = ({ children }: IIdleDetectionProviderProps) => {
  const { webViewPlatform, supportsLocalAuth } = useWebView();
  const cmAuthEnabled = pageDataService.isCMAuthCheckEnabled(webViewPlatform, supportsLocalAuth) && pageDataService.isCMOnboarded();

  return cmAuthEnabled ? (
    <IdleDetectionProviderInternal>{children}</IdleDetectionProviderInternal>
  ) : (
    <NoopProvider>{children}</NoopProvider>
  );
};
